import { useCallback } from 'react';
import { userAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';

/**
 * Custom hook for managing user deletion and dependencies
 */
export const useUserDeletion = (users, loadUsers, getErrorMessage) => {
  const { showSuccess, showError } = useToast();

  /**
   * Fetch user dependencies
   */
  const fetchUserDependencies = useCallback(async (userId, setDependencyTree, setLoadingDependencies) => {
    try {
      setLoadingDependencies(true);
      const dependencies = await userAPI.getDependencies(userId);
      setDependencyTree(dependencies);
    } catch (err) {
      console.error('Error fetching dependencies:', err);
      // If API doesn't support dependencies endpoint, create basic info
      try {
        const user = users.find(u => u.id === userId);
        if (user) {
          setDependencyTree({
            roles: user.roles || [],
            sessions: [],
            auditLogs: 0,
            createdRecords: 0
          });
        }
      } catch (manualErr) {
        console.error('Error manually fetching dependencies:', manualErr);
        showError('Could not fetch dependency information');
        setDependencyTree({ roles: [], sessions: [], auditLogs: 0, createdRecords: 0 });
      }
    } finally {
      setLoadingDependencies(false);
    }
  }, [users, showError]);

  /**
   * Confirm and execute user deletion
   */
  const confirmDeleteUser = useCallback(async (selectedUser, onClose) => {
    if (!selectedUser) return;

    try {
      await userAPI.delete(selectedUser.id);
      showSuccess(`User "${selectedUser.name}" deleted successfully!`);
      onClose();
      await loadUsers(); // Reload the list
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to delete user');
      showError(`Failed to delete user: ${errorMessage}`);
    }
  }, [loadUsers, getErrorMessage, showSuccess, showError]);

  return {
    fetchUserDependencies,
    confirmDeleteUser
  };
};

